import { AxiosResponse } from 'axios';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'libs/Axios';
import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';

export const CUBE_TO_SUBJECT_DEFINITION_QUERY_KEY = {
  LIST: 'CUBE_TO_SUBJECT_DEFINITION_LIST',
  DETAILS: 'CUBE_TO_SUBJECT_DEFINITION_DETAILS',
  RUN: 'CUBE_TO_SUBJECT_DEFINITION_RUN',
};

export type CubeToSubjectDefinitionStatus = 'ACTIVE' | 'INACTIVE';

export type CubeToSubjectRunStatus = 'IN_QUEUE' | 'IN_PROGRESS' | 'COMPLETED' | 'SUCCESS' | 'FAILED' | string;

export interface CubeToSubjectRunMemberItem {
  memberId: string;
  memberName: string;
  recordSourceId?: string;
  status?: 'EXECUTED' | 'EXCLUDED' | string;
  reason?: string;
}

export interface CubeToSubjectRun {
  id: string;
  definitionId: string;
  status: CubeToSubjectRunStatus;
  progressPercent: number;
  totalMembers?: number;
  processedMembers?: number;
  totalExecuted?: number;
  totalExcluded?: number;
  currentMember?: CubeToSubjectRunMemberItem | null;
  processedMemberItems?: CubeToSubjectRunMemberItem[];
  processedMemberItemsLimit?: number;
  message?: string | null;
  errorMessage?: string | null;
  result?: unknown;
  triggeredBy?: string | null;
  triggeredAt?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  createdDate?: string;
  lastModifiedDate?: string;
}

const RUNNING_RUN_STATUSES = new Set<string>(['IN_QUEUE', 'IN_PROGRESS']);

// A run is finished when it reports a finish time or a non-running status.
export const isRunTerminal = (run?: CubeToSubjectRun | null) =>
  !!run && (!!run.finishedAt || !RUNNING_RUN_STATUSES.has(run.status));

export const isRunFailed = (run?: CubeToSubjectRun | null) =>
  !!run && (run.status === 'FAILED' || run.status === 'ERROR' || !!run.errorMessage);

export interface CubeToSubjectDefinitionTarget {
  subjectId: string;
  // Display name, present on responses.
  subjectName?: string;
  tableDefinitionId: string;
}

export interface CubeToSubjectDefinitionSource {
  cubeAttributeId: string;
  // Display names below are present on responses.
  cubeAttributeName?: string;
  cubeDefinitionId: string;
  cubeName?: string;
  cubeLayerId: string;
  cubeLayerName?: string;
  timeInterval: string;
  timePeriodValue: string;
}

export interface RequestCubeToSubjectDefinition {
  name: string;
  status: CubeToSubjectDefinitionStatus;
  target: CubeToSubjectDefinitionTarget;
  source: CubeToSubjectDefinitionSource;
}

export interface CubeToSubjectDefinition extends RequestCubeToSubjectDefinition {
  id: string;
  // Present while a run is active for this definition.
  currentRunId?: string | null;
  // The most recent run (finished or otherwise) for this definition.
  latestRun?: CubeToSubjectRun | null;
  createdDate?: string;
  lastModifiedDate?: string;
}

const BASE_URL = '/api/v1/function-calculation/cube-to-subject-definitions';

export function useGetCubeToSubjectDefinitions(enabled = true) {
  return useQuery<AxiosResponse<CubeToSubjectDefinition[]>, AxiosDefaultErrorEntity>(
    [CUBE_TO_SUBJECT_DEFINITION_QUERY_KEY.LIST],
    () => axios.get(BASE_URL).then((res) => res),
    { enabled },
  );
}

export function useGetCubeToSubjectDefinition(definitionId?: string) {
  return useQuery<AxiosResponse<CubeToSubjectDefinition>, AxiosDefaultErrorEntity>(
    [CUBE_TO_SUBJECT_DEFINITION_QUERY_KEY.DETAILS, definitionId],
    () => axios.get(`${BASE_URL}/${definitionId}`).then((res) => res),
    { enabled: !!definitionId },
  );
}

export function useCreateCubeToSubjectDefinition() {
  const queryClient = useQueryClient();
  return useMutation<
    AxiosResponse<CubeToSubjectDefinition>,
    AxiosDefaultErrorEntity,
    RequestCubeToSubjectDefinition
  >((body) => axios.post(BASE_URL, body).then((res) => res), {
    onSuccess: () => {
      queryClient.invalidateQueries([CUBE_TO_SUBJECT_DEFINITION_QUERY_KEY.LIST]);
    },
  });
}

export function useUpdateCubeToSubjectDefinition() {
  const queryClient = useQueryClient();
  return useMutation<
    AxiosResponse<CubeToSubjectDefinition>,
    AxiosDefaultErrorEntity,
    { definitionId: string; body: RequestCubeToSubjectDefinition }
  >(({ definitionId, body }) => axios.put(`${BASE_URL}/${definitionId}`, body).then((res) => res), {
    onSuccess: () => {
      queryClient.invalidateQueries([CUBE_TO_SUBJECT_DEFINITION_QUERY_KEY.LIST]);
    },
  });
}

export function useTriggerCubeToSubjectDefinition() {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<CubeToSubjectRun>, AxiosDefaultErrorEntity, { definitionId: string }>(
    ({ definitionId }) => axios.post(`${BASE_URL}/${definitionId}/trigger`).then((res) => res),
    {
      onSuccess: () => {
        // Reflect the newly queued run (currentRunId) on the definition list.
        queryClient.invalidateQueries([CUBE_TO_SUBJECT_DEFINITION_QUERY_KEY.LIST]);
      },
    },
  );
}

export function useGetCubeToSubjectRun(runId?: string | null, enabled = true) {
  return useQuery<AxiosResponse<CubeToSubjectRun>, AxiosDefaultErrorEntity>(
    [CUBE_TO_SUBJECT_DEFINITION_QUERY_KEY.RUN, runId],
    () => axios.get(`${BASE_URL}/runs/${runId}`).then((res) => res),
    {
      enabled: !!runId && enabled,
      // Poll every 3s until the run finishes.
      refetchInterval: (data) => (data?.data && isRunTerminal(data.data) ? false : 3000),
      cacheTime: 0,
    },
  );
}

export function useDeleteCubeToSubjectDefinition() {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<CubeToSubjectDefinition>, AxiosDefaultErrorEntity, { definitionId: string }>(
    ({ definitionId }) => axios.delete(`${BASE_URL}/${definitionId}`).then((res) => res),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CUBE_TO_SUBJECT_DEFINITION_QUERY_KEY.LIST]);
      },
    },
  );
}
